import type { Breadcrumb, Event } from '@sentry/vue'

type EventRequest = NonNullable<Event['request']>

const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi
const REDACTED = '[redacted]'
const URL_DATA_KEYS = ['url', 'from', 'to']

const redactText = (text: string): string => text.replace(EMAIL_PATTERN, REDACTED)

const withoutQuery = (url: string): string => url.split('?')[0]

function redactRequest(request: EventRequest): EventRequest {
	const headers = { ...request.headers }

	if (headers.Referer !== undefined) {
		headers.Referer = withoutQuery(headers.Referer)
	}

	return {
		...request,
		url: request.url === undefined ? undefined : withoutQuery(request.url),
		query_string: undefined,
		headers,
	}
}

export function redactBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
	// A console call's raw arguments include whole request errors, and the request body and headers with them.
	const { arguments: _consoleArguments, ...data } = breadcrumb.data ?? {}

	URL_DATA_KEYS.filter(key => typeof data[key] === 'string').forEach(key => {
		data[key] = withoutQuery(data[key])
	})

	return {
		...breadcrumb,
		message: breadcrumb.message === undefined ? undefined : redactText(breadcrumb.message),
		data,
	}
}

export function redactEvent<T extends Event>(event: T): T {
	event.exception?.values?.forEach(exception => {
		exception.value = exception.value === undefined ? undefined : redactText(exception.value)
	})

	if (event.message !== undefined) {
		event.message = redactText(event.message)
	}

	if (event.request !== undefined) {
		event.request = redactRequest(event.request)
	}

	return event
}
